import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { useIssuer, useSaveIssuer, useSaveIssuerLogo, type Issuer } from "@/hooks/use-wholesale";

type BankRow = { bankName: string; accountNumber: string };

const FIELDS: { key: keyof Issuer & string; label: string }[] = [
  { key: "companyName", label: "Naziv firme" },
  { key: "legalName", label: "Puni pravni naziv" },
  { key: "address", label: "Adresa" },
  { key: "postalCode", label: "Poštanski broj" },
  { key: "city", label: "Grad" },
  { key: "country", label: "Država" },
  { key: "phone", label: "Telefon" },
  { key: "email", label: "Email" },
  { key: "website", label: "Web" },
  { key: "idNumber", label: "ID broj" },
  { key: "vatNumber", label: "PDV broj" },
  { key: "registrationNumber", label: "Matični broj" },
];

const emptyValues = () => Object.fromEntries(FIELDS.map((f) => [f.key, ""])) as Record<string, string>;

/** Podaci firme koja izdaje predračun i otpremnicu — naziv, kontakti, identifikatori, žiro računi i logo. */
export function IssuerForm() {
  const { data, isLoading, error } = useIssuer();
  const save = useSaveIssuer();
  const saveLogo = useSaveIssuerLogo();
  const fileInput = useRef<HTMLInputElement>(null);

  const [values, setValues] = useState<Record<string, string>>(emptyValues);
  const [banks, setBanks] = useState<BankRow[]>([]);
  const [dirty, setDirty] = useState(false);

  // Server puni formu samo dok korisnik nije ništa dirao, da invalidacija nakon
  // spremanja (ili nakon uploada loga) ne pregazi unos u toku.
  useEffect(() => {
    if (!data || dirty) return;
    setValues(Object.fromEntries(FIELDS.map((f) => [f.key, String(data[f.key] ?? "")])));
    setBanks(data.banks.map((b) => ({ bankName: b.bankName, accountNumber: b.accountNumber })));
  }, [data, dirty]);

  const set = (key: string, value: string) => {
    setDirty(true);
    setValues((v) => ({ ...v, [key]: value }));
  };

  const setBank = (i: number, patch: Partial<BankRow>) => {
    setDirty(true);
    setBanks((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  async function handleLogo(file: File | null) {
    try {
      await saveLogo.mutateAsync(file);
      toast.success(file ? "Logo učitan" : "Logo uklonjen");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri učitavanju loga");
    } finally {
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function handleSave() {
    if (!values.companyName?.trim()) {
      toast.error("Naziv firme je obavezan");
      return;
    }
    try {
      await save.mutateAsync({ ...values, banks } as any);
      setDirty(false);
      toast.success("Podaci izdavaoca spremljeni");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri spremanju podataka");
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
      </div>
    );
  }

  if (error) return <p className="text-destructive text-xs">{(error as Error).message}</p>;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Podaci izdavaoca</p>
        <p className="text-[11px] text-muted-foreground">
          Idu na predračun, otpremnicu i javni link. Bez njih dokumenti izlaze bez zaglavlja.
        </p>
      </div>

      <div className="flex items-center gap-3">
        {data?.hasLogo ? (
          // `updatedAt` u URL-u razbija keš pregledača kad se logo zamijeni.
          <img
            src={`/api/wholesale/issuer/logo?v=${data.updatedAt}`}
            alt="Logo"
            className="h-10 max-w-40 object-contain ring-1 ring-foreground/10 p-1"
          />
        ) : (
          <div className="h-10 w-40 ring-1 ring-foreground/10 grid place-items-center text-[10px] text-muted-foreground">
            Bez loga
          </div>
        )}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={saveLogo.isPending} onClick={() => fileInput.current?.click()}>
            <Upload className="size-3.5" /> Učitaj logo
          </Button>
          {data?.hasLogo && (
            <Button size="sm" variant="ghost" disabled={saveLogo.isPending} onClick={() => handleLogo(null)}>
              Ukloni
            </Button>
          )}
        </div>
        <input
          ref={fileInput}
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleLogo(file);
          }}
        />
      </div>
      <p className="text-[11px] text-muted-foreground -mt-2">PNG ili JPEG, najviše 512 KB.</p>

      <div className="grid grid-cols-2 gap-2">
        {FIELDS.map((f) => (
          <div key={f.key} className="flex flex-col gap-1">
            <Label htmlFor={`issuer-${f.key}`} className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
              {f.label}
            </Label>
            <Input
              id={`issuer-${f.key}`}
              className="h-8 text-xs"
              value={values[f.key] ?? ""}
              onChange={(e) => set(f.key, e.target.value)}
            />
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Bankovni računi</p>
        {banks.length === 0 && <p className="text-[11px] text-muted-foreground">Nema unesenih računa.</p>}
        {banks.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input
              className="h-8 text-xs flex-1"
              placeholder="Naziv banke"
              value={b.bankName}
              onChange={(e) => setBank(i, { bankName: e.target.value })}
            />
            <Input
              className="h-8 text-xs flex-1 tabular-nums"
              placeholder="Broj računa"
              value={b.accountNumber}
              onChange={(e) => setBank(i, { accountNumber: e.target.value })}
            />
            <Button
              size="icon"
              variant="ghost"
              className="size-8 shrink-0"
              aria-label="Ukloni račun"
              onClick={() => {
                setDirty(true);
                setBanks((rows) => rows.filter((_, idx) => idx !== i));
              }}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ))}
        <div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setDirty(true);
              setBanks((rows) => [...rows, { bankName: "", accountNumber: "" }]);
            }}
          >
            <Plus className="size-3.5" /> Dodaj račun
          </Button>
        </div>
      </div>

      <div>
        <Button size="sm" onClick={handleSave} disabled={save.isPending}>
          {save.isPending ? "Spremanje…" : "Sačuvaj podatke izdavaoca"}
        </Button>
      </div>
    </div>
  );
}
